// workAreas/view.js
import React, { useEffect } from 'react';
import {
  Alert,
  Snackbar,
  CircularProgress,
  Box,
  Typography,
  Collapse,
  Divider,
  Fade,
  Button
} from '@mui/material';
import {
  Save as SaveIcon,
  Add as AddIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Desk as DeskIcon,
  MeetingRoom as MeetingRoomIcon,
  Circle as CircleIcon
} from '@mui/icons-material';

// Import Tab Content Components
import ServicesTab from '../components/services';
import GeneralInfo from '../components/generalInfo';
import GalleryTab from '../components/gallery';
import PricingTab from '../components/workArea/prices';

// Import the new WorkAreaDialog component
import WorkAreaDialog from '../../dialogs/WorkAreaDialog';

import {
  StyledContainer,
  StyledHeader,
  StyledTitle,
  StyledWorkAreaPaper,
  StyledWorkAreaHeader,
  StyledWorkAreaDetails,
  StyledSectionContent,
  StyledLoadingContainer,
  StyledEmptyState,
  StyledFloatingSaveContainer,
  StyledFloatingSaveWrapper,
  StyledFloatingSaveCancelButton,
  StyledFloatingSaveButton
} from './styled';

const WorkAreasView = ({
  // Original props
  venueInfo,
  parentIsLoading,
  resourceTypeFilter, // 'HD' for Hot Desks, 'MR' for Meeting Rooms
  expandedWorkAreas,
  showCreateDialog,
  prices,
  isUpdatingPrice,
  snackbar,
  
  // GeneralInfo props
  generalInfoFormData,
  generalInfoHasChanges,
  isSavingGeneralInfo,
  
  // Services props
  servicesFormData,
  servicesHasChanges,
  isSavingServices,
  
  // Gallery props - simplified for immediate operations
  galleryFormData,
  newImages,
  isSavingGallery,
  
  // Computed values
  allWorkAreas,
  hasWorkAreas,
  VAT_RATE,
  
  // Handlers
  handleToggleWorkArea,
  handleCloseCreateDialog,
  handleCreateWorkArea,
  handlePriceChangeWithoutVAT,
  handlePriceChangeWithVAT,
  showSnackbar,
  handleCloseSnackbar,
  
  // GeneralInfo handlers
  handleGeneralInfoFormChange,
  handleSaveGeneralInfo,
  handleCancelGeneralInfo,
  
  // Services handlers
  handleServicesFormChange,
  handleSaveServices,
  handleCancelServices,
  handleServiceMoveUp,
  handleServiceMoveDown,
  handleServiceDescriptionSave,
  handleAddService,
  handleRemoveService,
  
  // Gallery handlers - immediate operations
  handleSaveGallery, // Only for uploading new images
  handleCancelGallery, // Only for cancelling pending uploads
  handleImageUpload,
  handleImageDelete,
  handleImageMoveLeft,
  handleImageMoveRight,
  
  // Bulk activation handler
  handleBulkActivateResources,
  
  // External dependencies
  updateManagementWorkArea
}) => {

  // Auto-expand the only work area if there is just one and it's not already expanded
  useEffect(() => {
    if (allWorkAreas.length === 1) {
      const onlySlug = allWorkAreas[0].slug;
      if (!expandedWorkAreas[onlySlug]) {
        handleToggleWorkArea(onlySlug);
      }
    }
  }, [allWorkAreas, expandedWorkAreas, handleToggleWorkArea]);
  
  const renderLoadingState = () => (
    <StyledContainer>
      <StyledHeader>
        <StyledTitle>
          Loading venue information...
        </StyledTitle>
      </StyledHeader>
    </StyledContainer>
  );

  const renderNoVenueInfo = () => (
    <StyledContainer>
      <StyledHeader>
        <StyledTitle>
          Venue information not available
        </StyledTitle>
      </StyledHeader>
    </StyledContainer>
  );

  const renderNoWorkAreasState = () => (
    <Box>
      <StyledEmptyState severity="info" sx={{ mb: 3 }}>
        No work areas have been added yet. Create your first work area to start adding hot desks and meeting rooms.
      </StyledEmptyState>
      <Box display="flex" justifyContent="center">
        <Button
          variant="contained"
          color="primary"
          size="large"
          startIcon={<AddIcon />}
          onClick={() => handleToggleWorkArea('create')}
        >
          Add Work Area
        </Button>
      </Box>
      {renderWorkAreaDialog()}
      {renderSnackbar()}
    </Box>
  );

  const renderGeneralInfoSection = (workArea) => {
    const formData = generalInfoFormData[workArea.slug] || {
      name: '',
      description: '',
      capacity: 0,
      status: 'AC'
    };
    
    // Get only filtered resources for this work area
    const filteredResources = getFilteredResources(workArea);
    const pendingResources = filteredResources.filter(r => r.status === 'PP');
    const hasPendingResources = pendingResources.length > 0;

    const handleBulkActivate = async (resources) => {
      try {
        const result = await handleBulkActivateResources(workArea.slug, resourceTypeFilter);
        
        if (result && result.success) {
          showSnackbar(result.message || `Successfully activated ${result.activatedCount} resources`, 'success');
        } else {
          showSnackbar(result?.error || 'Failed to activate resources', 'error');
        }
      } catch (error) {
        console.error('Error in bulk activation:', error);
        showSnackbar('An error occurred while activating resources', 'error');
      }
    };

    const resourceTypeLabel = getResourceTypeLabel();

    return (
      <StyledSectionContent>
        <GeneralInfo
          target={{
            type: 'workArea',
            slug: workArea.slug
          }}
          formData={formData}
          onFormChange={(newFormData) => 
            handleGeneralInfoFormChange(workArea.slug, newFormData)
          }
          canActivate={true}
        />
      </StyledSectionContent>
    );
  };

  const renderFloatingSaveButton = (workArea) => {
    const hasGeneralInfoChanges = generalInfoHasChanges[workArea.slug];
    const hasServicesChanges = servicesHasChanges[workArea.slug];
    const hasNewImages = newImages[workArea.slug] && newImages[workArea.slug].length > 0;
    
    // For immediate operations, we only show save button for:
    // 1. General info changes
    // 2. Services changes  
    // 3. New images to upload
    const hasChanges = hasGeneralInfoChanges || hasServicesChanges || hasNewImages;
    const isSaving = isSavingGeneralInfo || isSavingServices || isSavingGallery;
    
    if (!hasChanges && !isSaving) return null;

    const handleSaveAll = async () => {
      try {
        if (hasGeneralInfoChanges) {
          await handleSaveGeneralInfo(workArea.slug);
        }
        if (hasServicesChanges) {
          await handleSaveServices(workArea.slug);
        }
        if (hasNewImages) {
          await handleSaveGallery(workArea.slug);
        }
      } catch (error) {
        console.error('Error saving changes:', error);
      }
    };

    const handleCancelAll = () => {
      if (hasGeneralInfoChanges) {
        handleCancelGeneralInfo(workArea.slug);
      }
      if (hasServicesChanges) {
        handleCancelServices(workArea.slug);
      }
      if (hasNewImages) {
        handleCancelGallery(workArea.slug);
      }
    };

    return (
      <StyledFloatingSaveContainer>
        <Fade in={!isSaving}>
          <StyledFloatingSaveWrapper>
            <StyledFloatingSaveCancelButton
              variant="outlined"
              onClick={handleCancelAll}
            >
              Cancel
            </StyledFloatingSaveCancelButton>
            
            <StyledFloatingSaveButton
              variant="contained"
              size="large"
              startIcon={<SaveIcon />}
              onClick={handleSaveAll}
            >
              Save Changes
            </StyledFloatingSaveButton>
          </StyledFloatingSaveWrapper>
        </Fade>

        <Fade in={isSaving}>
          <StyledFloatingSaveWrapper>
            <StyledFloatingSaveButton
              variant="contained"
              size="large"
              disabled
              startIcon={<CircularProgress size={20} sx={{ color: 'white' }} />}
            >
              Saving...
            </StyledFloatingSaveButton>
          </StyledFloatingSaveWrapper>
        </Fade>
      </StyledFloatingSaveContainer>
    );
  };

  const renderServicesSection = (workArea) => {
    const services = servicesFormData[workArea.slug] || [];

    return (
      <StyledSectionContent>
        <ServicesTab
          workArea={workArea}
          resource={null}
          selectedServices={services}
          onServicesChange={(newServices) => handleServicesFormChange(workArea.slug, newServices)}
          onServiceMoveUp={(serviceSlug) => handleServiceMoveUp(workArea.slug, serviceSlug)}
          onServiceMoveDown={(serviceSlug) => handleServiceMoveDown(workArea.slug, serviceSlug)}
          onServiceDescriptionSave={(serviceSlug, description) => handleServiceDescriptionSave(workArea.slug, serviceSlug, description)}
          onAddService={(service) => handleAddService(workArea.slug, service)}
          onRemoveService={(serviceSlug) => handleRemoveService(workArea.slug, serviceSlug)}
          updateManagementWorkArea={updateManagementWorkArea}
          showSnackbar={showSnackbar}
          onDataUpdate={() => {}}
          disableSaveButton={true}
          isSaving={isSavingServices}
        />
      </StyledSectionContent>
    );
  };

  const renderGallerySection = (workArea) => {
    const images = galleryFormData[workArea.slug] || [];
    const newImagesData = newImages[workArea.slug] || [];

    return (
      <StyledSectionContent>
        <GalleryTab
          workArea={workArea}
          resource={null}
          orderedImages={images}
          orderedNewImages={newImagesData}
          onImagesChange={() => {}} // No longer needed - operations are immediate
          onImageUpload={(files) => handleImageUpload(workArea.slug, files)}
          onImageDelete={(index, isNew) => handleImageDelete(workArea.slug, index, isNew)}
          onImageMoveLeft={(index, isNew) => handleImageMoveLeft(workArea.slug, index, isNew)}
          onImageMoveRight={(index, isNew) => handleImageMoveRight(workArea.slug, index, isNew)}
          updateResourceManagement={updateManagementWorkArea}
          showSnackbar={showSnackbar}
          onDataUpdate={() => {}}
          disableSaveButton={true} // Gallery operations are immediate
        />
      </StyledSectionContent>
    );
  };

  const renderPricingSection = (workArea) => (
    <StyledSectionContent>
      <PricingTab
        workArea={workArea}
        prices={prices}
        VAT_RATE={VAT_RATE}
        isUpdatingPrice={isUpdatingPrice}
        handlePriceChangeWithoutVAT={handlePriceChangeWithoutVAT}
        handlePriceChangeWithVAT={handlePriceChangeWithVAT}
        showSnackbar={showSnackbar}
        disableSaveButton={true}
        venueInfo={venueInfo}
        floor={workArea.floor}
      />
    </StyledSectionContent>
  );

  const renderWorkAreaContent = (workArea) => (
    <StyledWorkAreaDetails>
      {renderGeneralInfoSection(workArea)}
      
      <Divider sx={{ my: 3 }} />
      {renderServicesSection(workArea)}
      
      <Divider sx={{ my: 3 }} />
      {renderGallerySection(workArea)}
      
      <Divider sx={{ my: 3 }} />
      {renderPricingSection(workArea)}
      
      {renderFloatingSaveButton(workArea)}
    </StyledWorkAreaDetails>
  );

  // Helper to get resources based on filter
  const getFilteredResources = (workArea) => {
    if (resourceTypeFilter === 'HD') {
      return workArea.hot_desks || [];
    } else if (resourceTypeFilter === 'MR') {
      return workArea.meeting_rooms || [];
    } else if (resourceTypeFilter === 'DD') {
      return workArea.dedicated_desks || [];
    }
    // If no filter, return all
    return [
      ...(workArea.hot_desks || []),
      ...(workArea.meeting_rooms || []),
      ...(workArea.dedicated_desks || [])
    ];
  };

  // Get the label for the resource type
  const getResourceTypeLabel = () => {
    if (resourceTypeFilter === 'HD') return 'Hot Desk';
    if (resourceTypeFilter === 'MR') return 'Meeting Room';
    if (resourceTypeFilter === 'DD') return 'Dedicated Desk';
    return 'Resource';
  };

  // Get the icon for the resource type
  const getResourceTypeIcon = () => {
    if (resourceTypeFilter === 'MR') return <MeetingRoomIcon fontSize="small" color="action" />;
    return <DeskIcon fontSize="small" color="action" />;
  };

  const renderWorkAreaCard = (workArea) => {
    const isExpanded = !!expandedWorkAreas[workArea.slug];
    
    // Get only resources of the filtered type
    const filteredResources = getFilteredResources(workArea);
    const resourceCount = filteredResources.length;
    const pendingCount = filteredResources.filter(r => r.status === 'PP').length;
    const activeCount = filteredResources.filter(r => r.status === 'AC').length;
    
    // Skip work areas with no resources of this type
    if (resourceCount === 0) {
      return null;
    }
    
    // Get status color
    const getStatusColor = (status) => {
      switch (status) {
        case 'AC': return 'success.main';
        case 'PP': return 'warning.main';
        case 'IN': return 'error.main';
        default: return 'text.secondary';
      }
    };
    
    const getStatusLabel = (status) => {
      switch (status) {
        case 'AC': return 'Active';
        case 'PP': return 'Pending';
        case 'IN': return 'Inactive';
        default: return status;
      }
    };

    const resourceTypeLabel = getResourceTypeLabel();
    
    return (
      <StyledWorkAreaPaper
        key={workArea.slug}
        isExpanded={isExpanded}
      >
        {/* Clickable Header */}
        <StyledWorkAreaHeader
          isExpanded={isExpanded}
          onClick={() => handleToggleWorkArea(workArea.slug)}
        >
          <Box sx={{ flex: 1 }}>
            {/* Work Area Name and Status */}
            <Box display="flex" alignItems="center" gap={1} mb={0.5}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {workArea.name || 'Unnamed Work Area'}
              </Typography>
              <Box display="flex" alignItems="center" gap={0.5}>
                <CircleIcon sx={{ fontSize: 10, color: getStatusColor(workArea.status) }} />
                <Typography variant="caption" color="text.secondary">
                  {getStatusLabel(workArea.status)}
                </Typography>
              </Box>
            </Box>
            
            {/* Floor and Resource Summary - Only show filtered resource type */}
            <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
              <Typography variant="body2" color="text.secondary">
                Floor: {workArea.floor?.name || 'N/A'}
              </Typography>
              
              <Typography variant="body2" color="text.secondary">|</Typography>
              
              <Box display="flex" alignItems="center" gap={0.5}>
                {getResourceTypeIcon()}
                <Typography variant="body2" color="text.secondary">
                  {resourceCount} {resourceTypeLabel}{resourceCount !== 1 ? 's' : ''}
                  {pendingCount > 0 && (
                    <Typography component="span" variant="body2" color="warning.main">
                      {' '}({pendingCount} pending)
                    </Typography>
                  )}
                  {activeCount > 0 && pendingCount === 0 && (
                    <Typography component="span" variant="body2" color="success.main">
                      {' '}({activeCount} active)
                    </Typography>
                  )}
                </Typography>
              </Box>
            </Box>
          </Box>
          
          {/* Expand/Collapse Icon */}
          <Box sx={{ ml: 2 }}>
            {isExpanded ? (
              <ExpandLessIcon />
            ) : (
              <ExpandMoreIcon />
            )}
          </Box>
        </StyledWorkAreaHeader>
        
        {/* Collapsible Content */}
        <Collapse in={isExpanded} timeout={300}>
          <Box sx={{ p: 3, pt: 0 }}>
            {renderWorkAreaContent(workArea)}
          </Box>
        </Collapse>
      </StyledWorkAreaPaper>
    );
  };

  const renderWorkAreasList = () => (
    parentIsLoading ? (
      <StyledLoadingContainer>
        <CircularProgress />
        <Typography variant="h6" sx={{ mt: 2 }}>Loading work areas...</Typography>
      </StyledLoadingContainer>
    ) : allWorkAreas.length === 0 ? (
      <StyledEmptyState severity="info" sx={{ mb: 2 }}>
        No work areas have been added yet. Click "Add Work Area" to create your first one.
      </StyledEmptyState>
    ) : (
      allWorkAreas.map(renderWorkAreaCard)
    )
  );

  const renderWorkAreaDialog = () => (
    <WorkAreaDialog
      open={showCreateDialog}
      onClose={handleCloseCreateDialog}
      onSubmit={handleCreateWorkArea}
      workArea={{
        name: '',
        floor_slug: venueInfo?.floors?.[0]?.slug || '',
        area_type: 'OP',
        status: 'AC'
      }}
      floors={venueInfo?.floors || []}
      isEditing={false}
    />
  );

  const renderSnackbar = () => (
    <Snackbar
      open={snackbar.open}
      autoHideDuration={6000}
      onClose={handleCloseSnackbar}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
    >
      <Alert
        onClose={handleCloseSnackbar}
        severity={snackbar.severity}
        variant="filled"
        sx={{ width: '100%' }}
      >
        {snackbar.message}
      </Alert>
    </Snackbar>
  );

  // Simplified view - show the single work area content directly
  const renderSingleWorkAreaView = () => {
    // Get the first (and only) work area
    const workArea = allWorkAreas[0];
    
    if (!workArea) {
      return renderNoWorkAreasState();
    }

    // Check if this work area has resources of the filtered type
    const filteredResources = getFilteredResources(workArea);
    
    return (
      <Box>
        {/* Show work area content directly - no cards, no collapse */}
        {renderWorkAreaContent(workArea)}
        {renderWorkAreaDialog()}
        {renderSnackbar()}
      </Box>
    );
  };

  // Main render logic
  if (parentIsLoading) {
    return renderLoadingState();
  }

  if (!venueInfo) {
    return renderNoVenueInfo();
  }

  if (!hasWorkAreas) {
    return renderNoWorkAreasState();
  }

  return renderSingleWorkAreaView();
};

export default WorkAreasView;